"use client";

import { Recording } from "@/app/service/playbackService";

interface FeaturedRecordingsProps {
  recordings: Recording[];
  selectedRecording?: Recording | null;
  onRecordingSelect: (recording: Recording) => void;
  loadingPlayback?: boolean;
}

const FeaturedRecordings = ({
  recordings,
  selectedRecording,
  onRecordingSelect,
  loadingPlayback = false
}: FeaturedRecordingsProps) => {
  // Filter for featured recordings that are visible
  const featuredRecordings = recordings.filter(recording => recording.isVisible && recording.isFeatured);

  if (featuredRecordings.length === 0) {
    return null;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="relative bg-gradient-to-br from-widertalk-navy via-widertalk-navy to-blue-900 border-2 border-widertalk-navy rounded-xl p-4 overflow-hidden shadow-xl">
      {/* Animated background overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-30 animate-pulse"></div>

      {/* Decorative corner elements */}
      <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-white/10 to-transparent rounded-bl-full"></div>
      <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-white/5 to-transparent rounded-tr-full"></div>

      <div className="relative z-10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="relative">
              <svg className="h-6 w-6 text-yellow-400 mr-3 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full animate-ping"></div>
            </div>
            <h3 className="text-lg font-bold text-white tracking-wide">On Demand Replays</h3>
          </div>
          <div className="flex items-center space-x-2">
            <div className="relative">
              <span className="bg-gradient-to-r from-yellow-400 to-amber-500 text-gray-900 px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                {featuredRecordings.length}
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full blur opacity-30 animate-pulse"></div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {featuredRecordings.map((recording, index) => {
            const recordingName = recording.event?.description || 'Unknown Event';
            const recordingDate = formatDate(recording.startTime) || 'Unknown Date';
            const userDisplayName = recording.user?.displayName || 'Anonymous';
            const userAvatarUrl = recording.user?.avatarUrl;

            return (
              <div
                key={recording.compositionArn || `featured-${index}`}
                className={`group relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 transform hover:scale-[1.02] ${
                  selectedRecording?.compositionArn === recording.compositionArn
                    ? "border-yellow-400 bg-gradient-to-r from-white/20 to-white/10 shadow-2xl scale-[1.02]"
                    : "border-white/20 hover:border-yellow-400/60 hover:bg-gradient-to-r hover:from-white/15 hover:to-white/5 hover:shadow-xl"
                }`}
                onClick={() => onRecordingSelect(recording)}
                style={{
                  animationDelay: `${index * 100}ms`,
                  animation: 'slideInUp 0.6s ease-out forwards'
                }}
              >
                {/* Animated border glow */}
                <div className={`absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
                  selectedRecording?.compositionArn === recording.compositionArn ? 'opacity-100' : ''
                }`}
                  style={{
                    background: 'linear-gradient(45deg, transparent, rgba(250, 204, 21, 0.3), transparent)',
                    filter: 'blur(1px)'
                  }}
                ></div>

                {/* Featured badge with enhanced styling */}
                <div className="absolute -top-2 -right-2 z-20">
                  <div className="relative">
                    <div className="bg-gradient-to-r from-yellow-400 to-amber-500 p-2 rounded-full shadow-lg transform rotate-12 group-hover:rotate-0 transition-transform duration-300">
                      <svg className="h-4 w-4 text-gray-900" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                      </svg>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full blur opacity-50 animate-pulse"></div>
                  </div>
                </div>

                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 pr-8">
                      <h4 className="font-bold text-white text-base mb-1 group-hover:text-yellow-200 transition-colors duration-300 line-clamp-2">
                        {recordingName}
                      </h4>
                      <div className="flex items-center text-xs text-white group-hover:text-yellow-200 transition-colors duration-300">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>
                        </svg>
                        {recordingDate}
                      </div>
                    </div>
                  </div>

                  {/* Enhanced user info */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        {userAvatarUrl ? (
                          <img
                            src={userAvatarUrl}
                            alt={userDisplayName}
                            className="w-8 h-8 rounded-full border-2 border-white/30 group-hover:border-yellow-400/60 transition-colors duration-300"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center border-2 border-white/30 group-hover:border-yellow-400/60 transition-colors duration-300">
                            <span className="text-xs font-bold text-white">
                              {userDisplayName.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-white font-semibold group-hover:text-yellow-200 transition-colors duration-300">
                          {userDisplayName}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <svg className="w-4 h-4 text-white/60 group-hover:text-yellow-400 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>

                  {/* Event image with enhanced styling */}
                  {recording.event?.imageUrl && (
                    <div className="mt-4 relative">
                      <div className="relative overflow-hidden rounded-lg group">
                        <img
                          src={recording.event.imageUrl}
                          alt={recordingName}
                          className="w-full h-20 object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Loading state with enhanced animation */}
                  {loadingPlayback && selectedRecording?.compositionArn === recording.compositionArn && (
                    <div className="mt-3 flex items-center justify-center">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                        <span className="text-xs text-white/80 ml-2">Loading content...</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom gradient separator */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent opacity-60"></div>
    </div>
  );
};

export default FeaturedRecordings;
